import { Core } from "@faire/design-tokens";
import { ILeanProductTile } from "@faire/web-api/indigofair/data/ILeanProductTile";
import { formatMoney } from "@faire/web/common/money";
import { Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React, { useCallback, useState } from "react";
import styled from "styled-components";

const ANIMATION_DURATION_MS = 250;
const WHOLESALE_MSRP_SPACING = 4;

export const TilePriceBadge: React.FC<{
  productTile: ILeanProductTile | undefined;
}> = ({ productTile }) => {
  const [isHovering, setIsHovering] = useState(false);

  const wholesalePrice = productTile?.min_option_wholesale_price
    ? formatMoney({
        money: productTile.min_option_wholesale_price,
        omitCentsIfZero: true,
      })
    : undefined;

  const saleWholesalePrice = productTile?.min_option_wholesale_promo_price
    ?.discounted_item_price
    ? formatMoney({
        money:
          productTile.min_option_wholesale_promo_price.discounted_item_price,
        omitCentsIfZero: true,
      })
    : undefined;

  const actualWholesalePrice = saleWholesalePrice ?? wholesalePrice;

  const retailPrice = productTile?.min_option_retail_price
    ? formatMoney({
        money: productTile.min_option_retail_price,
        omitCentsIfZero: true,
      })
    : undefined;

  const { isDesktopAndAbove } = useViewport();

  const onMouseEnter = useCallback(() => {
    if (isDesktopAndAbove) {
      setIsHovering(true);
    }
  }, [isDesktopAndAbove]);

  const onMouseLeave = useCallback(() => {
    if (isDesktopAndAbove) {
      setIsHovering(false);
    }
  }, [isDesktopAndAbove]);

  return (
    <PriceBadge
      $maxWidth={
        isHovering
          ? // full width of tile minus margin on left/right
            `calc(100% - ${getSpacing("2x")})`
          : getDefaultWidthPx(actualWholesalePrice)
      }
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Typography variant="labelSansMedium">{actualWholesalePrice}</Typography>
      <Spacer size={WHOLESALE_MSRP_SPACING} />
      {retailPrice ? (
        <RetailPrice $visible={isHovering}>
          <StrictLocalMsg
            defaultMessage="MSRP {amountMoney}"
            description={{
              text: "Describes a manufacturer's suggested retail price (MSRP), which is the price that the brand recommends the product be sold for",
              img: "https://cdn.faire.com/fastly/ab4a6fe9466e5be954f97d93986ac9df176c84812bbb796a9ba88e23a17fc4b7.png",
            }}
            values={{
              amountMoney: retailPrice,
            }}
          />
        </RetailPrice>
      ) : null}
    </PriceBadge>
  );
};

const PriceBadge = styled(Row)<{ $maxWidth: string }>`
  transition: max-width ${ANIMATION_DURATION_MS}ms ease-in-out;
  background: ${Core.surface.muted};
  opacity: 90%;
  border-radius: 100px;
  position: absolute;
  bottom: ${getSpacing("1x")};
  left: ${getSpacing("1x")};
  padding: ${getSpacing(4, "1x")};
  max-width: ${({ $maxWidth }) => $maxWidth};

  &:hover {
    opacity: 100%;
  }
`;

const RetailPrice = styled(Typography).attrs({ variant: "labelSansRegular" })<{
  $visible: boolean;
}>`
  transition: opacity ${ANIMATION_DURATION_MS}ms ease-in-out;
  opacity: ${({ $visible }) => ($visible ? "100%" : "0%")};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

const getDefaultWidthPx = (wholesalePrice: string = "") => {
  const characterPxSize = wholesalePrice.length * 7.4;
  const padding = 2 * 8;
  // add left/right padding
  return `${characterPxSize + padding}px`;
};
